<main class="container">

  <!-- Not Connected State -->
  <app-not-connected 
    *ngIf="!isConnected"
    description="Connect a wallet to wrap or unwrap your CryptoPunks.">
  </app-not-connected>

  <!-- Connected State -->
  <div *ngIf="isConnected">
    <!-- General Operations -->
    <div *ngIf="!allPunksClaimed && isDevMode" class="section">
      <div *ngIf="isMounted" class="init-section">
        <h2 class="section-title">⚡ System Overview & Operations</h2>
        <div class="init-content">
          <div>
            <h3 class="subsection-title">Initialize Punks</h3>
            <button (click)="getAllPunks()" [disabled]="claimingAllPunks"
              class="btn init-btn">
              <span *ngIf="claimingAllPunks">Initializing Punks...</span>
              <span *ngIf="!claimingAllPunks">Initialize Punks</span>
            </button>
            <p class="init-description">
              <span *ngIf="claimingAllPunks">🔄 Distributing all 10,000 punks to test addresses (this may take a
                while)...</span>
              <span *ngIf="!claimingAllPunks">Distribute all 10,000 punks to test addresses</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="isMounted" class="section">
      <div>
        <div class="punk-section-header">
          <span class="punk-section-title">My Punks</span>
          <span class="punk-count">Total: {{ cachedTotalCount }} punks</span>
        </div>
        <!-- Loading state -->
        <div *ngIf="loadingMyPunks" class="loading-punks">
          <div class="loading-spinner"></div>
          <p class="loading-text">Loading your punks...</p>
        </div>

        <!-- No punks state -->
        <div *ngIf="!loadingMyPunks && !cachedHasAnyPunks" class="no-punks">
          <div class="no-punks-content">
            <p class="no-punks-text">This wallet does not own any CryptoPunks.</p>
            <p class="no-punks-subtext">Connect a different wallet or acquire some punks to use the wrapper.</p>
          </div>
        </div>

        <!-- Punks grid -->
        <div
          *ngIf="!loadingMyPunks && cachedHasAnyPunks"
          class="punk-grid">
          
          <div *ngFor="let punk of cachedAllPunks; trackBy: trackByPunk" (click)="selectPunk(punk.id, punk.source)"
            [class]="punk.classes" class="punk-card">
            <img [src]="'https://punkcdn.com/data/images/punk' + punk.id.toString().padStart(4, '0') + '.svg'"
              [alt]="'Punk #' + punk.id" class="punk-image" loading="lazy" />
            <div class="punk-id">#{{ punk.id }}</div>
            <div [class]="punk.textClass" class="punk-status">
              {{ punk.selected ? 'Selected' : punk.label }}
            </div>
            <div *ngIf="punk.selected" class="check-icon">
                <svg xmlns="http://www.w3.org/2000/svg" class="check-svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Operations Section -->
    <div class="section">
      <div>

        <!-- Show selected punks -->
        <div class="selection-panel" [class.empty]="!selectedPunks.length">
          <div *ngIf="selectedPunks.length" class="selected-content">
            <p class="selection-info">Selected {{ selectedPunks.length }} punk{{
              selectedPunks.length > 1 ? 's' : '' }} ( {{ selectedPunkType!.charAt(0).toUpperCase() + selectedPunkType!.slice(1) }} )</p>
            <div class="selected-punks">
              <div *ngFor="let punkId of selectedPunks" class="selected-punk-container">
                <img [src]="'https://punkcdn.com/data/images/punk' + punkId.toString().padStart(4, '0') + '.svg'"
                  [alt]="'Punk #' + punkId" class="selected-punk" />
              </div>
            </div>
          </div>
          <div *ngIf="!selectedPunks.length" class="empty-selection">
            <p class="empty-text">No punks selected</p>
            <p class="empty-subtext">Select a punk above to perform wrapping actions</p>
          </div>
        </div>

        <!-- Tab Navigation -->
        <div class="tabs">
          <div class="tab" [class.active]="activeTab === 'legacy'" (click)="activeTab = 'legacy'">
            🔗 Legacy Wrapper
          </div>
          <div class="tab" [class.active]="activeTab === 'c721'" (click)="activeTab = 'c721'">
            🚀 CryptoPunks721
          </div>
        </div>

      <div class="workflow-container">

        <!-- Legacy Workflow -->
        <div *ngIf="activeTab === 'legacy'" class="workflow">
          <div class="proxy-info">
            <p class="proxy-label">Legacy Proxy Address</p>
            <p class="proxy-address">
              <span *ngIf="validLegacyProxy">
                <a href="https://etherscan.io/address/{{ proxy }}" target="_blank">{{ proxy }} <ng-template [ngTemplateOutlet]="popupIcon"></ng-template></a>
              </span>
              <button *ngIf="!validLegacyProxy" (click)="registerProxyData()" 
                class="btn proxy-register-btn">
                Register Proxy
              </button>
            </p>
          </div>

          <!-- Legacy Flow Visualization -->
          <div class="workflow-card">
            <div class="workflow-header">
              <div class="workflow-title">LEGACY WORKFLOW</div>
              <div *ngIf="selectedPunks.length === 1" class="workflow-status success">1 punk selected</div>
              <div *ngIf="selectedPunks.length > 1" class="workflow-status error">Single punk only</div>
            </div>

            <!-- Flow Steps -->
            <div class="workflow-steps" [class.blur-disabled]="!canUseLegacyWorkflow || !validLegacyProxy">
              
              <!-- Step 1: Unwrapped Punk (Always Disabled) -->
              <div class="workflow-step">
                <div class="step-indicator inactive">
                  1
                </div>
                <div class="step-content">
                  <button disabled class="btn step-btn disabled">
                    Unwrapped Punk
                  </button>
                </div>
              </div>

              <!-- Step 2: Unwrapped → Proxy -->
              <div class="workflow-step" [class.active]="canTransferToProxy">
                <div class="step-indicator" [class.active]="canTransferToProxy" [class.inactive]="!canTransferToProxy">
                  2
                </div>
                <div class="step-content">
                  <button (click)="canTransferToProxy ? transferPunkToProxy() : null" [disabled]="!canTransferToProxy"
                    [class.btn]="true" [class.step-btn]="true" [class.active]="canTransferToProxy" [class.disabled]="!canTransferToProxy">
                    Transfer to Proxy
                  </button>
                </div>
              </div>

              <!-- Step 3: Proxy → Wrapped -->
              <div class="workflow-step" [class.active]="selectedPunkType === 'proxy' && selectedPunks.length === 1">
                <div class="step-indicator" [class.active]="selectedPunkType === 'proxy' && selectedPunks.length === 1" [class.inactive]="!(selectedPunkType === 'proxy' && selectedPunks.length === 1)">
                  3
                </div>
                <div class="step-content">
                  <button (click)="canWrapLegacy ? mintWrappedPunkBt() : null" [disabled]="!canWrapLegacy"
                    [class.btn]="true" [class.step-btn]="true" [class.active]="canWrapLegacy" [class.disabled]="!canWrapLegacy">
                    Mint Wrapped Punk
                  </button>
                </div>
              </div>

              <!-- Step 4: Wrapped → Unwrap -->
              <div class="workflow-step" [class.active]="selectedPunkType === 'legacy-wrapped'">
                <div class="step-indicator" [class.active]="selectedPunkType === 'legacy-wrapped'" [class.inactive]="selectedPunkType !== 'legacy-wrapped'">
                  4
                </div>
                <div class="step-content">
                  <button (click)="canUnwrapLegacy ? burnWrappedPunkBt() : null" [disabled]="!canUnwrapLegacy"
                    [class.btn]="true" [class.step-btn]="true" [class.active]="canUnwrapLegacy" [class.disabled]="!canUnwrapLegacy">
                    Unwrap Punk
                  </button>
                </div>
              </div>
            </div>

            <!-- Status Messages -->
            <div *ngIf="!validLegacyProxy || (selectedPunkType && selectedPunks.length >= 1)"
              class="status-panel"
              [class.warning]="!validLegacyProxy || selectedPunkType === 'c721-wrapped' || selectedPunkType === 'stash' || (selectedPunks.length > 1 && selectedPunkType !== 'legacy-wrapped')"
              [class.error]="!validLegacyProxy || selectedPunkType === 'c721-wrapped' || selectedPunkType === 'stash' || (selectedPunks.length > 1 && selectedPunkType !== 'legacy-wrapped')"
              [class.success]="validLegacyProxy && (selectedPunkType === 'unwrapped' || selectedPunkType === 'proxy' || selectedPunkType === 'legacy-wrapped') && !(selectedPunks.length > 1 && selectedPunkType !== 'legacy-wrapped')">
              <span *ngIf="!validLegacyProxy" class="error-text">⚠️ Please register a proxy first to use Legacy wrapper</span>
              
              <ng-container *ngIf="validLegacyProxy">
                <!-- Check multi-punk limitation second -->
                <span *ngIf="selectedPunks.length > 1 && selectedPunkType !== 'legacy-wrapped'" class="error-text">Legacy wrapper only supports single punk operations. Please select only one punk.</span>
                
                <!-- Only show other statuses if not multi-punk limitation -->
                <ng-container *ngIf="!(selectedPunks.length > 1 && selectedPunkType !== 'legacy-wrapped')">
                  <span *ngIf="selectedPunkType === 'unwrapped'" class="success-text">✓ Punk is in your wallet (ready to transfer to proxy)</span>
                  <span *ngIf="selectedPunkType === 'proxy'" class="success-text">✓ Punk is in proxy (ready to wrap)</span>
                  <span *ngIf="selectedPunkType === 'legacy-wrapped'" class="success-text">✓ Punk is wrapped with Legacy wrapper (ready to unwrap)</span>
                  <span *ngIf="selectedPunkType === 'c721-wrapped'" class="error-text">Punk is wrapped with C721 - cannot use Legacy wrapper</span>
                  <span *ngIf="selectedPunkType === 'stash'" class="error-text">Punk is in stash - cannot use Legacy wrapper (switch to C721 tab)</span>
                </ng-container>
              </ng-container>
            </div>
          </div>
        </div>

        <!-- C721 Workflow -->
        <div *ngIf="activeTab === 'c721'" class="workflow">
          <div class="proxy-info">
            <p class="proxy-label">C721 Proxy Address</p>
            <p class="proxy-address">
              <span *ngIf="c721Proxy">
                <a href="https://etherscan.io/address/{{ c721Proxy }}" target="_blank">{{ c721Proxy }} <ng-template [ngTemplateOutlet]="popupIcon"></ng-template></a>
              </span>
              <span *ngIf="!c721Proxy">
                Loading...
              </span>
            </p>
          </div>

          <!-- C721 Flow Visualization -->
          <div class="workflow-card">
            <div class="workflow-header">
              <div class="workflow-title">C721 WORKFLOW</div>
              <div *ngIf="selectedPunks.length" class="workflow-status success">{{ selectedPunks.length }} punk{{ selectedPunks.length > 1 ? 's' : '' }} selected</div>
            </div>

            <!-- Flow Steps -->
            <div class="workflow-steps" [class.blur-disabled]="!canUseC721Workflow">
              
              <!-- Step 1: Unwrapped Punk (Always Disabled) -->
              <div class="workflow-step">
                <div class="step-indicator inactive">
                  1
                </div>
                <div class="step-content">
                  <button disabled class="btn step-btn disabled">
                    Unwrapped Punk
                  </button>
                </div>
              </div>

              <!-- Step 2: Unwrapped → Stash -->
              <div class="workflow-step" [class.active]="selectedPunkType === 'unwrapped'">
                <div class="step-indicator" [class.active]="selectedPunkType === 'unwrapped'" [class.inactive]="selectedPunkType !== 'unwrapped'">
                  2
                </div>
                <div class="step-content">
                  <button (click)="canTransferToStash ? transferToStashUnified() : null"
                    [disabled]="!canTransferToStash"
                    [class.btn]="true" [class.step-btn]="true" [class.active]="canTransferToStash" [class.disabled]="!canTransferToStash">
                    {{ selectedPunks.length > 1 ? 'Batch Transfer to Stash' : 'Transfer to Stash' }}
                  </button>
                </div>
              </div>

              <!-- Step 3: Stash → Wrapped -->
              <div class="workflow-step" [class.active]="selectedPunkType === 'stash'">
                <div class="step-indicator" [class.active]="selectedPunkType === 'stash'" [class.inactive]="selectedPunkType !== 'stash'">
                  3
                </div>
                <div class="step-content">
                  <button (click)="canWrapC721 ? wrapUnified() : null" [disabled]="!canWrapC721"
                    [class.btn]="true" [class.step-btn]="true" [class.active]="canWrapC721" [class.disabled]="!canWrapC721">
                    {{ selectedPunks.length > 1 ? 'Batch Wrap' : 'Wrap Punk' }}
                  </button>
                </div>
              </div>

              <!-- Step 4: Wrapped → Unwrap -->
              <div class="workflow-step" [class.active]="selectedPunkType === 'c721-wrapped'">
                <div class="step-indicator" [class.active]="selectedPunkType === 'c721-wrapped'" [class.inactive]="selectedPunkType !== 'c721-wrapped'">
                  4
                </div>
                <div class="step-content">
                  <button (click)="canUnwrapC721 ? unwrapUnified() : null" [disabled]="!canUnwrapC721"
                    [class.btn]="true" [class.step-btn]="true" [class.active]="canUnwrapC721" [class.disabled]="!canUnwrapC721">
                    {{ selectedPunks.length > 1 ? 'Batch Unwrap' : 'Unwrap Punk' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Status Messages -->
            <div *ngIf="selectedPunkType" class="status-panel"
              [class.warning]="selectedPunkType === 'proxy' || selectedPunkType === 'legacy-wrapped'"
              [class.error]="selectedPunkType === 'proxy' || selectedPunkType === 'legacy-wrapped'"
              [class.success]="selectedPunkType === 'unwrapped' || selectedPunkType === 'stash' || selectedPunkType === 'c721-wrapped'">
              <span *ngIf="selectedPunkType === 'unwrapped'" class="success-text">✓ Punks are in your wallet (ready to transfer to stash)</span>
              <span *ngIf="selectedPunkType === 'stash'" class="success-text">✓ Punks are in stash (ready to wrap)</span>
              <span *ngIf="selectedPunkType === 'c721-wrapped'" class="success-text">✓ Punks are wrapped with C721 (ready to unwrap)</span>
              <span *ngIf="selectedPunkType === 'proxy'" class="error-text">Punks are in Legacy proxy - cannot use C721 wrapper</span>
              <span *ngIf="selectedPunkType === 'legacy-wrapped'" class="error-text">Punks are wrapped with Legacy wrapper - cannot use C721 wrapper</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>

    <!-- Transaction Loading Overlay -->
    <div *ngIf="isTransactionLoading" class="loading-overlay">
      <div class="loading-card">
        <div class="loading-spinner"></div>
        <h3 class="loading-title">Transaction in Progress</h3>
        <p class="loading-text">
          <span *ngIf="currentTransactionType === 'register-proxy'">Registering proxy...</span>
          <span *ngIf="currentTransactionType === 'transfer'">Transferring punk(s)...</span>
          <span *ngIf="currentTransactionType === 'wrap'">Wrapping punk(s)...</span>
          <span *ngIf="currentTransactionType === 'unwrap'">Unwrapping punk(s)...</span>
          <span *ngIf="!currentTransactionType">Processing transaction...</span>
        </p>
        <p class="loading-subtitle">Please wait and do not close this window</p>
      </div>
    </div>
  </div>
</main> 


<ng-template #popupIcon>
  <svg class="popup-icon" _ngcontent-ng-c2943368829="" height="12" viewBox="0 0 12 12" width="12" xmlns="http://www.w3.org/2000/svg"><path _ngcontent-ng-c2943368829="" d="m4 0v2h-2v8h8v-2h2v4h-12v-12zm2 6v2h-2v-2zm2 0h-2v-2h2v-2h-2v-2h6v6h-2v-2h-2z" fill-rule="evenodd"></path></svg>
</ng-template>